<template>
  <v-dialog max-width="500" v-model="dialog">
    <template v-slot:activator="{ props: activatorProps }">
      <AdminOverrideButton
        color="primary"
        variant="tonal"
        label="Class Type Override"
        prepend-icon="mdi-lock"
      >
        <!-- @click="handleClick" -->
        <v-btn
          v-bind="activatorProps"
          color="primary"
          variant="tonal"
          text="Class Type Override"
        />
      </AdminOverrideButton>
    </template>

    <template v-slot:default="{ isActive }">
      <v-card
        title="Class Type Override"
        :subtitle="person?.name"
        color="background"
        :loading="isLoading"
        :disabled="isLoading"
      >
        <v-card-text class="d-flex flex-column ga-2">
          <v-select
            v-model="program"
            label="Program"
            hide-details="auto"
            :items="programTypes.filter((a) => a.id == 1 || a.id == 2)"
            item-title="name"
            item-value="id"
          />

          <v-select
            v-model="className"
            :disabled="!program"
            label="Class Type"
            hide-details="auto"
            :items="classTypeList"
            item-title="name"
            item-value="id"
          />

          <v-select
            v-model="overrideType"
            :disabled="!program"
            label="Override Type"
            hide-details="auto"
            :items="overrideTypeItems"
            item-title="title"
            item-value="value"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn text="Cancel" @click="isActive.value = false"></v-btn>

          <v-btn
            text="Save"
            color="primary"
            variant="flat"
            @click="handleSave"
            :disabled="!className || !overrideType"
          />
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script lang="ts" setup>
import type { PersonStaff as Person } from "../../../cbi-generator/generated/dto/PersonStaff";
import { useAppData } from "@/composables/useAppData";
import { usePersonAPI } from "@/composables/people/usePersonAPI";
import { DirtyFlag } from "@/composables/useDirtyFlag";
import { ClassEligibilityTypeEnum } from "@/autogen";

// Each override waives exactly one eligibility check for this person/type/season.
const overrideTypeItems: { title: string; value: ClassEligibilityTypeEnum }[] =
  [
    { title: "No Prerequisite", value: ClassEligibilityTypeEnum.PREREQ },
    { title: "Overqualified", value: ClassEligibilityTypeEnum.OVERKILL },
    {
      title: "Already In Another Instance",
      value: ClassEligibilityTypeEnum.SAME_TYPE_OTHER_INSTANCE,
    },
    { title: "Time Conflict", value: ClassEligibilityTypeEnum.TIME_CONFLICT },
    { title: "No Membership", value: ClassEligibilityTypeEnum.MEMBERSHIP },
  ];

const { programTypes, apClassTypes, jpClassTypes } = useAppData();
const { sendPersonClassOverride } = usePersonAPI();
const classDataState = inject<DirtyFlag | undefined>("classDataState");
const person = inject<Ref<Person | null>>("person");

const emits = defineEmits(["overrideAdded"]);

const className = ref<number | null>(null);
const overrideType = ref<ClassEligibilityTypeEnum | null>(null);
const program = ref<number | null>();
const classTypeList = computed(() => {
  return program.value === 1 ? apClassTypes : jpClassTypes;
});

const dialog = ref(false);
const isLoading = ref(false);

const handleClick = () => {
  dialog.value = true;
};

const handleSave = async () => {
  if (person?.value?.personId) {
    if (program.value && className.value && overrideType.value) {
      isLoading.value = true;
      await sendPersonClassOverride(
        person.value.personId,
        className.value,
        program.value,
        overrideType.value,
      );
      classDataState?.update(true);
      isLoading.value = dialog.value = false;
      emits("overrideAdded");
    }
  }
};

watchEffect(() => {
  if (!dialog.value) {
    className.value = overrideType.value = null;
  }
});
</script>
